import { hasAnyPermission, hasPermission, PERMISSIONS } from "../constants/permissions.ts";

export const ASSISTANT_ACCESS_PERMISSIONS = [PERMISSIONS.ORDERS_VIEW, PERMISSIONS.USERS_VIEW] as const;

export interface AssistantCapabilities {
  orders: boolean;
  users: boolean;
}

export const NO_ASSISTANT_CAPABILITIES: AssistantCapabilities = { orders: false, users: false };

export function hasAssistantAccess(permissions: number | undefined | null): boolean {
  return hasAnyPermission(permissions, ASSISTANT_ACCESS_PERMISSIONS);
}

export function capabilitiesFromPermissions(
  permissions: number | undefined | null,
): AssistantCapabilities {
  return {
    orders: hasPermission(permissions, PERMISSIONS.ORDERS_VIEW),
    users: hasPermission(permissions, PERMISSIONS.USERS_VIEW),
  };
}

export function parseAssistantCapabilities(raw: unknown): Partial<AssistantCapabilities> | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const source = raw as Record<string, unknown>;
  const parsed: Partial<AssistantCapabilities> = {};
  if (typeof source.orders === "boolean") parsed.orders = source.orders;
  if (typeof source.users === "boolean") parsed.users = source.users;
  return Object.keys(parsed).length > 0 ? parsed : null;
}

export function resolveAssistantCapabilities(
  server: Partial<AssistantCapabilities> | null | undefined,
  permissions: number | undefined | null,
): AssistantCapabilities {
  const fallback = capabilitiesFromPermissions(permissions);
  return {
    orders: typeof server?.orders === "boolean" ? server.orders : fallback.orders,
    users: typeof server?.users === "boolean" ? server.users : fallback.users,
  };
}

export interface AssistantScopeCopy {
  title: string;
  description: string;
  panelDescription: string;
  footer: string;
}

export function describeAssistantScope(capabilities: AssistantCapabilities): AssistantScopeCopy {
  if (capabilities.orders && capabilities.users) {
    return {
      title: "Ask about orders and customers",
      description:
        "Claude can search orders and customers, read order timelines, payment logs and customer activity, check carts, track shipments and spot stuck orders. It's read-only and can't change anything.",
      panelDescription:
        "Chat with Claude about orders and customers. Claude has read-only access to order and customer data.",
      footer:
        "Read-only access to orders and customers. Claude can make mistakes, so double-check before acting.",
    };
  }
  if (capabilities.users) {
    return {
      title: "Ask about customers",
      description:
        "Claude can search customers, summarize profiles and order history, and read customer activity, carts and wishlists. It's read-only and can't change anything.",
      panelDescription:
        "Chat with Claude about customers. Claude has read-only access to customer data.",
      footer:
        "Read-only access to customers. Claude can make mistakes, so double-check before acting.",
    };
  }
  if (capabilities.orders) {
    return {
      title: "Ask about orders",
      description:
        "Claude can search orders, read timelines and payment logs, track shipments and spot stuck orders. It's read-only and can't change anything.",
      panelDescription:
        "Chat with Claude about orders. Claude has read-only access to order data.",
      footer:
        "Read-only access to orders. Claude can make mistakes, so double-check before acting.",
    };
  }
  return {
    title: "Ask Claude",
    description:
      "Claude can look up the data your role has access to. It's read-only and can't change anything.",
    panelDescription: "Chat with Claude. Claude has read-only access to the data your role allows.",
    footer: "Read-only access. Claude can make mistakes, so double-check before acting.",
  };
}
